import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';

const EmailCopyField: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const email = 'evidence+lite_platform@solas-os.ai';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-white rounded-lg px-4 py-3 border border-gray-300 text-sm text-gray-700 font-mono">
        {email}
      </div>
      <button
        onClick={handleCopy}
        className={`flex-shrink-0 font-medium py-3 px-4 rounded-lg border transition-all inline-flex items-center gap-2 ${
          copied
            ? 'bg-green-50 border-green-300 text-green-700'
            : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
        }`}
        title="Copy to clipboard"
      >
        {copied ? (
          <>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm">Copied!</span>
          </>
        ) : (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const tiles = [
    {
      id: 'analytics',
      title: 'View Analytics',
      description: 'Explore your communication patterns and insights',
      buttonText: 'View Analytics',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      iconBg: 'bg-indigo-500',
      buttonColor: 'bg-indigo-500 hover:bg-indigo-600',
      onClick: () => navigate('/analytics'),
    },
    {
      id: 'analyse',
      title: 'Comms Checker',
      description: 'Analyse new communication for insights',
      buttonText: 'Launch Checker',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      iconBg: 'bg-green-500',
      buttonColor: 'bg-green-500 hover:bg-green-600',
      onClick: () => navigate('/analyse'),
    },
    {
      id: 'history',
      title: 'History',
      description: 'View your past analysis submissions',
      buttonText: 'View History',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      iconBg: 'bg-purple-500',
      buttonColor: 'bg-purple-500 hover:bg-purple-600',
      onClick: () => navigate('/history'),
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Welcome Back</h1>
          <p className="text-base sm:text-lg text-gray-600">
            What would you like to do today?
          </p>
        </div>

        {/* Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {tiles.map((tile) => (
            <div
              key={tile.id}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-20 h-20 ${tile.iconBg} rounded-2xl mb-6 text-white`}>
                {tile.icon}
              </div>

              {/* Content */}
              <div className="space-y-4">
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900">
                  {tile.title}
                </h3>

                {/* Description */}
                <p className="text-base text-gray-600 leading-relaxed min-h-[3rem]">
                  {tile.description}
                </p>

                {/* Button */}
                <button
                  onClick={tile.onClick}
                  className={`w-full ${tile.buttonColor} text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-opacity-50 inline-flex items-center justify-center gap-2`}
                >
                  <span>{tile.buttonText}</span>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Optional: Quick stats or info section */}
        <div className="mt-12 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 sm:p-8 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Continue to grow</h3>
              <p className="text-sm text-gray-600 mb-3">
                Submit your latest communication for analysis to learn about how you are leading with your communications.
              </p>
              <button
                onClick={() => navigate('/analyse')}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
              >
                Start Analysing
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Analyse via Email section */}
        <div className="mt-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 sm:p-8 border border-orange-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Analyse via Email
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Forward your communications here for instant analysis.
              </p>
              <EmailCopyField />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

